// The catalog of placeable item types (shirts, goals, balls, cones, fields, tools, lines) that
// populate the menus and seed the Fabric objects. Pure data — the `src` values are runtime asset
// URLs resolved by Fabric, not bundler imports.
//
// The dugout-vs-rinus choice is made per request via `getItemTypes(theme)` at the bottom of this file.
// This build-time read is only for the shirt filenames below: frf and fmf rebrand the shirt ASSETS
// rather than swapping the catalog, and that axis is still fixed per build.
import { FALLBACK_THEME, isTheme, type Theme } from './theme';

// Keyed by theme so a new one cannot inherit another federation's kit by falling through a
// conditional. The three entries are this app's slot order -- attacker, defender, neutral -- and
// frf and fmf permute them as well as renaming them, which is only visible when the slots are read
// together.
const SHIRT_ASSETS: Record<Theme, readonly [string, string, string]> = {
  knvb: ['orange', 'blue', 'yellow'],
  frf: ['frf-yellow', 'frf-red', 'frf-blue'],
  fmf: ['frf-yellow', 'frf-red', 'frf-blue'],
  default: ['orange', 'blue', 'yellow'],
  dugout: ['orange', 'blue', 'yellow'],
  sportwide: ['orange', 'blue', 'yellow'],
  worldcoaches: ['orange', 'blue', 'yellow'],
  // The school product names these by the colour they render in rather than by a playing role, so
  // the kit has to be the one the nl/en fc-gymles bundles describe: "Team oranje", "Team blauw".
  'fc-gymles': ['orange', 'blue', 'yellow'],
};

const buildTheme = process.env.NEXT_PUBLIC_THEME;
const theme: Theme = isTheme(buildTheme) ? buildTheme : FALLBACK_THEME;
const [attackerShirt, defenderShirt, neutralShirt] = SHIRT_ASSETS[theme];

export interface IconRef {
  src: string;
}

// A single menu/canvas item definition. Fields vary per category (a field has `dimensions`, a
// line has a `description`), so most are optional; `variation` (the stable id/key) and
// `defaultIcon` are the only constants.
export interface ItemType {
  name?: string;
  variation: string;
  defaultIcon: IconRef;
  // The symbol shown in Dugout's `alternateIcon` mode (triangle/square/circle); selected at render
  // time via `item[iconType]`. Absent for the other themes (they have no shape toggle).
  alternateIcon?: IconRef;
  button?: IconRef;
  color?: string;
  fontColor?: string;
  // Dugout number-text offset from the item origin (px). Absent elsewhere.
  fontTop?: number;
  fontLeft?: number;
  width?: number;
  height?: number;
  text?: string;
  dimensions?: number[];
  pitchType?: string;
  type?: 'indoor' | 'outdoor';
  fieldTypes?: string[];
  iconType?: string;
  description?: string;
  mobileLabel?: string;
  originX?: string;
  originY?: string;
}

const rinusShirtTypes: ItemType[] = [
  {
    name: 'Aanvaller',
    variation: 'shirt-attack',
    defaultIcon: {
      src: `assets/shirt-${attackerShirt}.svg`,
    },
    color: '#FFF865',
    fontColor: '#FFFFFF',
    width: 40,
    height: 40,
  },
  {
    name: 'Verdediger',
    variation: 'shirt-defend',
    defaultIcon: {
      src: `assets/shirt-${defenderShirt}.svg`,
    },
    color: '#E14F4F',
    fontColor: '#FFFFFF',
    width: 40,
    height: 40,
  },
  {
    name: 'Neutraal',
    variation: 'shirt-neutral',
    defaultIcon: {
      src: `assets/shirt-${neutralShirt}.svg`,
    },
    color: '#00AADB',
    fontColor: '#FFFFFF',
    width: 40,
    height: 40,
  },
  {
    name: 'Trainer',
    variation: 'shirt-trainer',
    defaultIcon: {
      src: 'assets/shirt-black.svg',
    },
    color: '#B0E0EE',
    fontColor: '#FFFFFF',
    width: 40,
    height: 40,
  },
];

// Dugout's six players. `shirt-attack`/`shirt-defend` intentionally repeat across teams (jouw team +
// extra team share a variation, distinguished by `color`/`name`) — matching the Dugout serialization
// so existing Dugout drawings import. Trainer/Scheidsrechter reuse `shirt.svg` and are told apart in
// the menu by `button` (shirt-t/shirt-s). Sizes/colours from the Dugout tool's `assets/assets.js`.
const dugoutShirtTypes: ItemType[] = [
  {
    name: 'Speler jouw team',
    variation: 'shirt-attack',
    defaultIcon: { src: 'assets/dugout/shirt.svg' },
    alternateIcon: { src: 'assets/dugout/triangle.svg' },
    color: '#FFF865',
    fontColor: '#000000',
    fontTop: 3,
    fontLeft: 0,
    width: 20,
    height: 19,
  },
  {
    name: 'Speler tegenstander',
    variation: 'shirt-defend',
    defaultIcon: { src: 'assets/dugout/shirt-opponent.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    color: '#E14F4F',
    fontColor: '#FFFFFF',
    fontTop: 0,
    fontLeft: -1,
    width: 20,
    height: 19,
  },
  {
    name: 'Speler extra team 1',
    variation: 'shirt-attack',
    defaultIcon: { src: 'assets/dugout/shirt.svg' },
    alternateIcon: { src: 'assets/dugout/triangle.svg' },
    color: '#00AADB',
    fontColor: '#000000',
    fontTop: 3,
    fontLeft: 0,
    width: 20,
    height: 19,
  },
  {
    name: 'Speler extra team 2',
    variation: 'shirt-defend',
    defaultIcon: { src: 'assets/dugout/shirt-opponent.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    color: '#B0E0EE',
    fontColor: '#FFFFFF',
    fontTop: 0,
    fontLeft: -1,
    width: 20,
    height: 19,
  },
  {
    name: 'Trainer',
    variation: 'shirt-trainer',
    defaultIcon: { src: 'assets/dugout/shirt.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    button: { src: 'assets/dugout/shirt-t.svg' },
    color: '#333333',
    fontColor: '#FFFFFF',
    fontTop: 3,
    fontLeft: 1,
    width: 20,
    height: 19,
  },
  {
    name: 'Scheidsrechter',
    variation: 'shirt-scheidsrechter',
    defaultIcon: { src: 'assets/dugout/shirt.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    button: { src: 'assets/dugout/shirt-s.svg' },
    color: '#333333',
    fontColor: '#FFFFFF',
    fontTop: 3,
    fontLeft: 1,
    width: 20,
    height: 19,
  },
];

const rinusGoalTypes: ItemType[] = [
  // Nothing in a gym hall is 7.32m wide, so the school product drops the senior goal and keeps the
  // three smaller ones.
  ...(theme === 'fc-gymles'
    ? []
    : [
        {
          name: 'Senioren doel',
          variation: 'goal-01',
          defaultIcon: {
            src: 'assets/goal-seniors.svg',
          },
          width: 110,
          height: 39,
          originX: 'center' as const,
          originY: 'center' as const,
          fieldTypes: ['outdoor' as const],
        },
      ]),
  {
    name: 'Pupillen doel',
    variation: 'goal-02',
    defaultIcon: {
      src: 'assets/goal-juniors.svg',
    },
    width: 87,
    height: 31,
    fieldTypes: ['outdoor'],
  },
  {
    name: 'Zaalvoetbal doel',
    variation: 'goal-04',
    defaultIcon: {
      src: 'assets/goal-indoor.svg',
    },
    width: 58,
    height: 33,
    fieldTypes: ['indoor'],
  },
  {
    name: 'Mini doel',
    variation: 'goal-03',
    defaultIcon: {
      src: 'assets/goal-mini.svg',
    },
    width: 44,
    height: 25,
    fieldTypes: ['outdoor', 'indoor'],
  },
];

// Dugout's goals: senioren/pupillen/mini + the Futsal goal (net in rinus). No indoor/outdoor
// `fieldTypes` — Dugout gates goals by the pitch (`pitchType`), wired with the fields in a later phase.
const dugoutGoalTypes: ItemType[] = [
  {
    name: 'Senioren doel',
    variation: 'goal-01',
    defaultIcon: { src: 'assets/dugout/goal01.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    width: 36,
    height: 12,
  },
  {
    name: 'Pupillen doel',
    variation: 'goal-02',
    defaultIcon: { src: 'assets/dugout/goal02.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    width: 24,
    height: 10,
  },
  {
    name: 'Mini doel',
    variation: 'goal-03',
    defaultIcon: { src: 'assets/dugout/goal03.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    width: 16,
    height: 6,
  },
  {
    name: 'Futsal doel',
    variation: 'goal-futsal',
    defaultIcon: { src: 'assets/dugout/goalFutsal.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    width: 19,
    height: 8,
  },
];

// The gym hall's own equipment. `name` mirrors the Dutch label the way it does for the football
// items, but it is the locale bundle that renders (`DRWNG__ITEM_<variation>`); `korfbal` keeps its
// legacy variation id because saved drawings reference it, even though the artwork is a plain foam
// ball.
const gymItemTypes: ItemType[] = [
  {
    name: 'Foambal',
    variation: 'korfbal',
    defaultIcon: {
      src: 'assets/korfbal.svg',
    },
    width: 15,
    height: 17,
  },
  {
    name: 'Korf',
    variation: 'korfbal-basket',
    defaultIcon: {
      src: 'assets/korfbal-basket.svg',
    },
    width: 28,
    height: 21,
  },
  {
    name: 'Kast',
    variation: 'vaulting-box',
    defaultIcon: {
      src: 'assets/vaulting-box.svg',
    },
    width: 36,
    height: 44,
  },
  {
    name: 'Bank',
    variation: 'bench',
    defaultIcon: {
      src: 'assets/bench.svg',
    },
    width: 89,
    height: 26,
  },
  {
    name: 'Dunne mat',
    variation: 'thin-mat',
    defaultIcon: {
      src: 'assets/thin-mat.svg',
    },
    width: 40,
    height: 44,
  },
  {
    name: 'Dikke mat',
    variation: 'thick-mat',
    defaultIcon: {
      src: 'assets/thick-mat.svg',
    },
    width: 80,
    height: 100,
  },
  {
    name: 'Lange mat',
    variation: 'long-mat',
    defaultIcon: {
      src: 'assets/long-mat.svg',
    },
    width: 68,
    height: 162,
  },
];

const rinusItemTypes: ItemType[] = [
  {
    name: 'Voetbal',
    variation: 'ball',
    defaultIcon: {
      src: 'assets/ball.svg',
    },
    width: 15,
    height: 15,
  },
  ...(theme === 'fc-gymles' ? gymItemTypes : []),
];

// Dugout groups the ball together with the Pion (round cone) as its non-cap items; both 10×10.
const dugoutItemTypes: ItemType[] = [
  {
    name: 'Voetbal',
    variation: 'ball',
    defaultIcon: { src: 'assets/dugout/ball.svg' },
    alternateIcon: { src: 'assets/dugout/circle.svg' },
    width: 10,
    height: 10,
  },
  {
    name: 'Pion',
    variation: 'witches-hat',
    defaultIcon: { src: 'assets/dugout/witches-hat.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    width: 10,
    height: 10,
  },
];

const rinusConeTypes: ItemType[] = [
  {
    name: 'Pion',
    variation: 'witches-hat',
    defaultIcon: {
      src: 'assets/cone.svg',
    },
    width: 15,
    height: 15,
  },
  {
    name: 'Wit hoedje',
    variation: 'cone-white',
    color: '#DDDDDD',
    defaultIcon: {
      src: 'assets/cone-white.svg',
    },
    width: 10,
    height: 10,
  },
  {
    name: 'Geel hoedje',
    variation: 'cone-yellow',
    color: '#ECCB17',
    defaultIcon: {
      src: 'assets/cone-yellow.svg',
    },
    width: 10,
    height: 10,
  },
  {
    name: 'Oranje hoedje',
    variation: 'cone-orange',
    color: '#F56200',
    defaultIcon: {
      src: 'assets/cone-orange.svg',
    },
    width: 10,
    height: 10,
  },
];

// Dugout's caps are one flat disc (`cone.svg`) recoloured per-cap by `color` (see addItem's gated
// recolour) — hence a fourth "purple" cap without needing a fourth asset. The round Pion lives in
// `itemTypes` for Dugout, not here.
const dugoutConeTypes: ItemType[] = [
  {
    name: 'Wit hoedje',
    variation: 'cone-white',
    color: '#DDDDDD',
    defaultIcon: { src: 'assets/dugout/cone.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    width: 10,
    height: 10,
  },
  {
    name: 'Geel hoedje',
    variation: 'cone-yellow',
    color: '#ECCB17',
    defaultIcon: { src: 'assets/dugout/cone.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    width: 10,
    height: 10,
  },
  {
    name: 'Oranje hoedje',
    variation: 'cone-orange',
    color: '#F56200',
    defaultIcon: { src: 'assets/dugout/cone.svg' },
    alternateIcon: { src: 'assets/dugout/circle.svg' },
    width: 10,
    height: 10,
  },
  {
    name: 'Paars hoedje',
    variation: 'cone-purple',
    color: '#DE4EA3',
    defaultIcon: { src: 'assets/dugout/cone.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    width: 10,
    height: 10,
  },
];

const rinusFieldTypes: ItemType[] = [
  {
    name: 'Veld 11 tegen 11',
    variation: 'field-01',
    defaultIcon: {
      src: 'assets/30mx45m.svg',
    },
    button: {
      src: 'assets/field1.svg',
    },
    width: 280,
    height: 432,
    text: '30x45',
    dimensions: [30, 45],
    pitchType: 'default',
    type: 'outdoor',
  },
  {
    name: 'Veld 11 tegen 11',
    variation: 'field-02',
    defaultIcon: {
      src: 'assets/15mx30m.svg',
    },
    button: {
      src: 'assets/field2.svg',
    },
    width: 204,
    height: 432,
    text: '15x30',
    dimensions: [15, 30],
    pitchType: 'default',
    type: 'outdoor',
  },
  {
    name: 'Veld 11 tegen 11',
    variation: 'field-03',
    defaultIcon: {
      src: 'assets/20mx20m.svg',
    },
    button: {
      src: 'assets/field3.svg',
    },
    width: 364,
    height: 398,
    text: '20x20',
    dimensions: [20, 20],
    pitchType: 'default',
    type: 'outdoor',
  },
  {
    name: 'Veld 11 tegen 11',
    variation: 'field-04',
    defaultIcon: {
      src: 'assets/20mx15m.svg',
    },
    button: {
      src: 'assets/field4.svg',
    },
    width: 420,
    height: 398,
    text: '20x15',
    dimensions: [20, 15],
    pitchType: 'default',
    type: 'outdoor',
  },
  {
    name: 'Veld 11 tegen 11',
    variation: 'room-01',
    defaultIcon: {
      src: 'assets/30mx45m-room.svg',
    },
    button: {
      src: 'assets/room1.svg',
    },
    width: 280,
    height: 432,
    text: '30x45',
    dimensions: [30, 45],
    pitchType: 'default',
    type: 'indoor',
  },
  {
    name: 'Veld 11 tegen 11',
    variation: 'room-02',
    defaultIcon: {
      src: 'assets/15mx30m-room.svg',
    },
    button: {
      src: 'assets/room2.svg',
    },
    width: 204,
    height: 432,
    text: '15x30',
    dimensions: [15, 30],
    pitchType: 'default',
    type: 'indoor',
  },
  {
    name: 'Veld 11 tegen 11',
    variation: 'room-03',
    defaultIcon: {
      src: 'assets/20mx20m-room.svg',
    },
    button: {
      src: 'assets/room3.svg',
    },
    width: 364,
    height: 398,
    text: '20x20',
    dimensions: [20, 20],
    pitchType: 'default',
    type: 'indoor',
  },
  {
    name: 'Veld 11 tegen 11',
    variation: 'room-04',
    defaultIcon: {
      src: 'assets/20mx15m-room.svg',
    },
    button: {
      src: 'assets/room4.svg',
    },
    width: 420,
    height: 398,
    text: '20x15',
    dimensions: [20, 15],
    pitchType: 'default',
    type: 'indoor',
  },
];

// Dugout's fields are full 615×615 lined pitches served as PNG backgrounds. Unlike rinus there is no
// indoor/outdoor `type` and no `dimensions`: the pitch is chosen by `pitchType` ('default' | 'futsal'),
// which the host supplies (URL param) rather than an in-tool switch, and all four are shown as a flat
// button grid. `defaultIcon` is the PNG `setField` paints as the canvas background (all four are fully
// opaque with their surround baked in — #69C292 for the default pitches, blue for futsal — matching the
// canvas fill set in Canvas.tsx); `button` uses the SVG for a crisp menu thumbnail. The SVG is the
// source artwork and the PNG is a rasterisation of it, so regenerate the PNG whenever the SVG changes.
const dugoutFieldTypes: ItemType[] = [
  {
    name: 'Veld 11 tegen 11',
    variation: 'field-01',
    defaultIcon: { src: 'assets/dugout/field01.png' },
    button: { src: 'assets/dugout/field01.svg' },
    width: 615,
    height: 615,
    text: '11x11',
    pitchType: 'default',
  },
  {
    name: 'Veld 8 tegen 8',
    variation: 'field-02',
    defaultIcon: { src: 'assets/dugout/field02.png' },
    button: { src: 'assets/dugout/field02.svg' },
    width: 615,
    height: 615,
    text: '8x8',
    pitchType: 'default',
  },
  {
    name: 'Veld 6 tegen 6',
    variation: 'field-03',
    defaultIcon: { src: 'assets/dugout/field03.png' },
    button: { src: 'assets/dugout/field03.svg' },
    width: 615,
    height: 615,
    text: '6x6',
    pitchType: 'default',
  },
  {
    name: 'Futsal',
    variation: 'field-futsal',
    defaultIcon: { src: 'assets/dugout/field-futsal.png' },
    button: { src: 'assets/dugout/field-futsal.svg' },
    width: 615,
    height: 615,
    text: 'Futsal',
    pitchType: 'futsal',
  },
];

const rinusToolTypes: ItemType[] = [
  {
    name: 'Volgorde',
    variation: 'shirt-annotation',
    defaultIcon: {
      src: 'assets/order-empty.svg',
    },
    button: {
      src: 'assets/order.svg',
    },
    iconType: 'order',
    fontColor: '#253780',
    width: 30,
    height: 29,
  },
  {
    name: 'Markeren',
    variation: 'marker',
    defaultIcon: {
      src: 'assets/mark-empty.svg',
    },
    button: {
      src: 'assets/mark.svg',
    },
    iconType: 'marker',
    color: 'rgba(0,0,0,0.1)',
    fontColor: '#FFFFFF',
    width: 20,
    height: 20,
  },
];

// Dugout's two tools: Volgorde (`shirt-annotation`, reuses rinus's annotation flow) + Markeren. The
// menu button icons are Dugout's `annotationIcon`/`highlightIcon`; the on-canvas graphic is
// `annotation`/`highlight`. Markeren keeps rinus's `marker` variation (not Dugout's internal
// `tool-highlight`) so it reuses the proven marker canvas behaviour (Canvas.tsx bring-to-front /
// translucency) and the `DRWNG__ITEM_marker` label with zero shared-component change — the variation
// string is never surfaced to the user. Tools are not recoloured (only shirts/cones are).
const dugoutToolTypes: ItemType[] = [
  {
    name: 'Volgorde',
    variation: 'shirt-annotation',
    defaultIcon: { src: 'assets/dugout/annotation.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    button: { src: 'assets/dugout/annotationIcon.svg' },
    color: 'rgba(0,0,0,0.1)',
    fontColor: '#FFFFFF',
    fontTop: 0,
    fontLeft: -1,
    width: 30,
    height: 29,
  },
  {
    name: 'Markeren',
    variation: 'marker',
    defaultIcon: { src: 'assets/dugout/highlight.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    button: { src: 'assets/dugout/highlightIcon.svg' },
    color: 'rgba(0,0,0,0.1)',
    fontColor: '#FFFFFF',
    fontTop: 0,
    fontLeft: -1,
    width: 20,
    height: 20,
  },
];

const lineTypes: ItemType[] = [
  {
    name: 'Lopen zonder bal',
    variation: 'line-dotted',
    description: 'Draw with dotted line',
    mobileLabel: 'Lopen',
    defaultIcon: {
      src: 'assets/dotted-line.svg',
    },
  },
  {
    name: 'Pass',
    variation: 'line-solid',
    description: 'Draw with solid line',
    mobileLabel: 'Pass',
    defaultIcon: {
      src: 'assets/solid-line.svg',
    },
  },
  {
    name: 'Dribbelen met bal',
    variation: 'line-squiggley',
    description: 'Draw with squiggley line',
    mobileLabel: 'Dribbelen',
    defaultIcon: {
      src: 'assets/squiggley-line.svg',
    },
  },
  {
    name: 'Schot',
    variation: 'line-shot',
    description: 'Draw with zig-zagged line',
    mobileLabel: 'Schot',
    defaultIcon: {
      src: 'assets/zig-zagged-line.svg',
    },
  },
];

const rinusDefaultTypes: ItemType[] = [
  {
    name: 'Aanvaller',
    variation: 'shirt-attack',
    defaultIcon: {
      src: `assets/shirt-${attackerShirt}.svg`,
    },
    color: '#FFF865',
    fontColor: '#FFFFFF',
    width: 40,
    height: 40,
  },
  {
    name: 'Geel hoedje',
    variation: 'cone-yellow',
    color: '#ECCB17',
    defaultIcon: {
      src: 'assets/cone-yellow.svg',
    },
    width: 10,
    height: 10,
  },
  {
    name: 'Voetbal',
    variation: 'ball',
    defaultIcon: {
      src: 'assets/ball.svg',
    },
    width: 15,
    height: 15,
  },
  {
    name: 'Pupillen doel',
    variation: 'goal-02',
    defaultIcon: {
      src: 'assets/goal-juniors.svg',
    },
    width: 87,
    height: 31,
  },
];

// Dugout's mobile QuickMenu quick-set: jouw-team shirt + a cap + ball + pupillen goal, drawn from the
// Dugout catalog (recolourable `shirt.svg`/`cone.svg` with their team `color`, so the QuickMenu
// thumbnails recolour via dugoutMenuIcon exactly like the main menu). QuickMenu is rinus mobile chrome
// (Dugout has none), so this mirrors the rinus quick-set shape with Dugout assets/sizes.
const dugoutDefaultTypes: ItemType[] = [
  {
    name: 'Speler jouw team',
    variation: 'shirt-attack',
    defaultIcon: { src: 'assets/dugout/shirt.svg' },
    alternateIcon: { src: 'assets/dugout/triangle.svg' },
    color: '#FFF865',
    fontColor: '#000000',
    fontTop: 3,
    fontLeft: 0,
    width: 20,
    height: 19,
  },
  {
    name: 'Geel hoedje',
    variation: 'cone-yellow',
    color: '#ECCB17',
    defaultIcon: { src: 'assets/dugout/cone.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    width: 10,
    height: 10,
  },
  {
    name: 'Voetbal',
    variation: 'ball',
    defaultIcon: { src: 'assets/dugout/ball.svg' },
    alternateIcon: { src: 'assets/dugout/circle.svg' },
    width: 10,
    height: 10,
  },
  {
    name: 'Pupillen doel',
    variation: 'goal-02',
    defaultIcon: { src: 'assets/dugout/goal02.svg' },
    alternateIcon: { src: 'assets/dugout/square.svg' },
    width: 24,
    height: 10,
  },
];

/** Every placeable catalog for one resolved theme. */
export interface ItemCatalogs {
  shirtTypes: ItemType[];
  goalTypes: ItemType[];
  itemTypes: ItemType[];
  coneTypes: ItemType[];
  fieldTypes: ItemType[];
  toolTypes: ItemType[];
  lineTypes: ItemType[];
  defaultTypes: ItemType[];
}

// Two frozen bundles rather than an object built per call: `app/page.tsx` stores these arrays in
// context state and closes over them in effects, so a stable identity per theme keeps those effects
// from re-running. `lineTypes` is theme-invariant and shared by both.
const RINUS_CATALOGS: ItemCatalogs = {
  shirtTypes: rinusShirtTypes,
  goalTypes: rinusGoalTypes,
  itemTypes: rinusItemTypes,
  coneTypes: rinusConeTypes,
  fieldTypes: rinusFieldTypes,
  toolTypes: rinusToolTypes,
  lineTypes,
  defaultTypes: rinusDefaultTypes,
};

const DUGOUT_CATALOGS: ItemCatalogs = {
  shirtTypes: dugoutShirtTypes,
  goalTypes: dugoutGoalTypes,
  itemTypes: dugoutItemTypes,
  coneTypes: dugoutConeTypes,
  fieldTypes: dugoutFieldTypes,
  toolTypes: dugoutToolTypes,
  lineTypes,
  defaultTypes: dugoutDefaultTypes,
};

/**
 * The catalogs for a RESOLVED theme (see config/theme.ts) — the runtime replacement for importing a
 * catalog directly, which would have baked in the build theme.
 *
 * Only the dugout-vs-rinus axis follows `resolvedTheme`. The frf/fmf shirt `src`s inside the rinus
 * arrays come from the build theme, so `getItemTypes('knvb')` on an frf build still yields frf
 * shirts — deliberate, and asserted in config/itemTypes.test.ts.
 */
export const getItemTypes = (
  resolvedTheme: string | undefined,
): ItemCatalogs =>
  resolvedTheme === 'dugout' ? DUGOUT_CATALOGS : RINUS_CATALOGS;
